import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { API_BASE_URL, SOCKET_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './ProjectsPage.css';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800';

const getProjectImage = (project) => {
  if (!project) return FALLBACK_IMAGE;
  // Try image field first
  if (project.image) return getImageUrl(project.image);
  // Try images array
  if (project.images) {
    try {
      const imgs = typeof project.images === 'string' ? JSON.parse(project.images) : project.images;
      if (Array.isArray(imgs) && imgs.length > 0) return getImageUrl(imgs[0]);
    } catch (_) {}
  }
  return FALLBACK_IMAGE;
};

const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = FALLBACK_IMAGE;
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PROJECTS_PER_PAGE = 20;
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle project click to navigate to details
  const handleProjectClick = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories
        const catRes = await fetch(`${API_BASE_URL}/categories`);
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData);
        }
        
        // Fetch projects
        const projRes = await fetch(`${API_BASE_URL}/projects`);
        if (projRes.ok) {
          const projData = await projRes.json();
          setProjects(projData.projects || projData || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Split categories for left and right columns
  const leftCategories = categories.slice(0, Math.ceil(categories.length / 2)).map((cat, i) => ({
    ...cat,
    active: i === 0
  }));
  const rightCategories = categories.slice(Math.ceil(categories.length / 2)).map(cat => ({
    ...cat,
    active: false
  }));

  // Get projects for display
  const featuredProject = projects[0] || {
    id: 1,
    title: 'Renovation',
    description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS',
    image: '/uploads/builder-images/4b61fb9908c3fc0a62b18864aee8ce03d13e04fe_w2104.png'
  };

  const sideProjects = projects.slice(1, 4).length > 0 ? projects.slice(1, 4) : [
    { id: 2, image: '/uploads/builder-images/5d0ef2e6b0ddc3c7b7b8ddc8546b0fc2ca26fc6a_w568.png' },
    { id: 3, image: '/uploads/builder-images/dfa323de11035416a213de014e57d4696d14b816_w568.png' },
    { id: 4, image: '/uploads/builder-images/dfa323de11035416a213de014e57d4696d14b816_w568.png' },
  ];

  const gridProjects = projects.slice(4, 10).length > 0 ? projects.slice(4, 10) : [
    { id: 5, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/5978a9edb3601e0311c66a4b92adb224c2796390_w875.png' },
    { id: 6, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/3708ed12cbb400ecbf7049427bf73efc1f5c6cd3_w875.png' },
    { id: 7, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/bdf28fac139b7aefbdcafb52da7f3cb6265a669a_w875.png' },
    { id: 8, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/bdf28fac139b7aefbdcafb52da7f3cb6265a669a_w875.png' },
    { id: 9, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/3708ed12cbb400ecbf7049427bf73efc1f5c6cd3_w875.png' },
    { id: 10, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/bdf28fac139b7aefbdcafb52da7f3cb6265a669a_w875.png' },
  ];

  const secondFeaturedProject = projects[10] || {
    id: 11,
    title: 'Renovation',
    description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS',
    image: '/uploads/builder-images/1c6ce2f8807d49c2c1ff453c1c9abd3fe1ae1438_w2104.png'
  };

  const secondSideProjects = projects.slice(11, 14).length > 0 ? projects.slice(11, 14) : [
    { id: 12, image: '/uploads/builder-images/8d31e82aad65ad7b71f75c1139a285a93c3381ac_w562.png' },
    { id: 13, image: '/uploads/builder-images/f378c0e39f19a3d2afb7516673fbd3526cefee5e_w562.png' },
    { id: 14, image: '/uploads/builder-images/f378c0e39f19a3d2afb7516673fbd3526cefee5e_w562.png' },
  ];

  const secondGridProjects = projects.slice(14, 20).length > 0 ? projects.slice(14, 20) : [
    { id: 15, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/017aa7460ea89ad7164890e6103f7e50fc982987_w873.png' },
    { id: 16, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/b163b631fb6bc98c872d9183991a439504deb37c_w873.png' },
    { id: 17, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/017aa7460ea89ad7164890e6103f7e50fc982987_w873.png' },
    { id: 18, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/017aa7460ea89ad7164890e6103f7e50fc982987_w873.png' },
    { id: 19, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/b163b631fb6bc98c872d9183991a439504deb37c_w873.png' },
    { id: 20, title: 'Renovation', description: 'Revitalization of The Relic Site of Yongle Palace / URBANUS', image: '/uploads/builder-images/017aa7460ea89ad7164890e6103f7e50fc982987_w873.png' },
  ];

  const BookmarkIcon = () => (
    <svg className="bookmark-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="4" fill="white" fillOpacity="0.7"/>
      <path d="M17 3H6.5C6.10218 3 5.72064 3.15804 5.43934 3.43934C5.15804 3.72064 5 4.10218 5 4.5V21C5.00007 21.1338 5.03595 21.2652 5.10393 21.3805C5.17191 21.4958 5.2695 21.5908 5.38659 21.6557C5.50367 21.7206 5.63598 21.7529 5.76978 21.7494C5.90358 21.7458 6.034 21.7066 6.1475 21.6356L11.75 18.1341L17.3534 21.6356C17.4669 21.7063 17.5972 21.7454 17.7309 21.7488C17.8646 21.7522 17.9967 21.7198 18.1136 21.655C18.2306 21.5902 18.3281 21.4953 18.396 21.3801C18.4639 21.2649 18.4998 21.1337 18.5 21V4.5C18.5 4.10218 18.342 3.72064 18.0607 3.43934C17.7794 3.15804 17.3978 3 17 3ZM17 19.6472L12.1466 16.6144C12.0274 16.5399 11.8896 16.5004 11.7491 16.5004C11.6085 16.5004 11.4708 16.5399 11.3516 16.6144L6.5 19.6472V4.5H17V19.6472Z" fill="#969696"/>
    </svg>
  );

  const CaretDownIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
    </svg>
  );

  const ChevronRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 12L6 8L10 4" stroke="#676767" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const ChevronRightPaginationIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 12L10 8L6 4" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const handleCategoryClick = () => {
    setShowCategoryModal(!showCategoryModal);
  };

  return (
    <div className="projects-page">
      <Header />
      
      <main className="projects-main">
        {/* Page Title Section */}
        <div className="projects-title-section">
          <h1 className="projects-page-title">
            <span className="title-primary">Project</span>
            <span className="title-separator"> | </span>
            <span className="title-result">2,342 result</span>
          </h1>
          <p className="projects-page-description">
            GulfConstructor connects suppliers, professionals, and contractors with live project opportunities across the region.
          </p>
        </div>

        {/* Content Section */}
        <div className="projects-content-section">
          <div className="projects-content-wrapper">
            {/* Breadcrumb */}
            <div className="projects-breadcrumb">
              <span className="breadcrumb-item">Home</span>
              <ChevronRightIcon />
              <span className="breadcrumb-item active">Project</span>
            </div>

            {/* Filter Bar */}
            <div className="projects-filter-bar">
              <div className="filter-dropdowns">
                <div className="filter-dropdown" onClick={handleCategoryClick}>
                  <span className="filter-label">Category</span>
                  <CaretDownIcon />
                </div>
                <div className="filter-dropdown">
                  <span className="filter-label">Sub Category</span>
                  <CaretDownIcon />
                </div>
                <div className="filter-dropdown">
                  <span className="filter-label">Country</span>
                  <CaretDownIcon />
                </div>
              </div>
            </div>
          </div>

          {/* Projects Grid Section */}
          <div className="projects-grid-section">
            {/* First Featured Section */}
            <div className="featured-section">
              <div className="featured-main" onClick={() => handleProjectClick(featuredProject.id)} style={{ cursor: 'pointer' }}>
                <div className="featured-image-wrapper">
                  <img 
                    src={getProjectImage(featuredProject)} 
                    alt={featuredProject.title}
                    className="featured-image"
                    onError={handleImageError}
                  />
                  <BookmarkIcon />
                </div>
                <div className="featured-info">
                  <h3 className="featured-title">{featuredProject.title}</h3>
                  <p className="featured-description">{featuredProject.description}</p>
                </div>
              </div>
              <div className="featured-side">
                {sideProjects.map((project) => (
                  <div key={project.id} className="side-project" onClick={() => handleProjectClick(project.id)} style={{ cursor: 'pointer' }}>
                    <div className="side-project-image-wrapper">
                      <img 
                        src={getProjectImage(project)} 
                        alt={project.title || 'Project'}
                        className="side-project-image"
                        onError={handleImageError}
                      />
                      <BookmarkIcon />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* First Grid Row */}
            <div className="projects-grid-row">
              {gridProjects.slice(0, 3).map((project) => (
                <div key={project.id} className="project-card" onClick={() => handleProjectClick(project.id)} style={{ cursor: 'pointer' }}>
                  <div className="project-card-image-wrapper">
                    <img 
                      src={getProjectImage(project)} 
                      alt={project.title}
                      className="project-card-image"
                      onError={handleImageError}
                    />
                    <BookmarkIcon />
                  </div>
                  <div className="project-card-info">
                    <h3 className="project-card-title">{project.title}</h3>
                    <p className="project-card-description">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Second Grid Row */}
            <div className="projects-grid-row">
              {gridProjects.slice(3, 6).map((project) => (
                <div key={project.id} className="project-card" onClick={() => handleProjectClick(project.id)} style={{ cursor: 'pointer' }}>
                  <div className="project-card-image-wrapper">
                    <img 
                      src={getProjectImage(project)} 
                      alt={project.title}
                      className="project-card-image"
                      onError={handleImageError}
                    />
                    <BookmarkIcon />
                  </div>
                  <div className="project-card-info">
                    <h3 className="project-card-title">{project.title}</h3>
                    <p className="project-card-description">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Second Projects Grid Section */}
          <div className="projects-grid-section">
            {/* Second Featured Section */}
            <div className="featured-section">
              <div className="featured-main" onClick={() => handleProjectClick(secondFeaturedProject.id)} style={{ cursor: 'pointer' }}>
                <div className="featured-image-wrapper">
                  <img 
                    src={getProjectImage(secondFeaturedProject)} 
                    alt={secondFeaturedProject.title}
                    className="featured-image"
                    onError={handleImageError}
                  />
                  <BookmarkIcon />
                </div>
                <div className="featured-info">
                  <h3 className="featured-title">{secondFeaturedProject.title}</h3>
                  <p className="featured-description">{secondFeaturedProject.description}</p>
                </div>
              </div>
              <div className="featured-side">
                {secondSideProjects.map((project) => (
                  <div key={project.id} className="side-project" onClick={() => handleProjectClick(project.id)} style={{ cursor: 'pointer' }}>
                    <div className="side-project-image-wrapper">
                      <img 
                        src={getProjectImage(project)} 
                        alt={project.title || 'Project'}
                        className="side-project-image"
                        onError={handleImageError}
                      />
                      <BookmarkIcon />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Third Grid Row */}
            <div className="projects-grid-row">
              {secondGridProjects.slice(0, 3).map((project) => (
                <div key={project.id} className="project-card" onClick={() => handleProjectClick(project.id)} style={{ cursor: 'pointer' }}>
                  <div className="project-card-image-wrapper">
                    <img 
                      src={getProjectImage(project)} 
                      alt={project.title}
                      className="project-card-image"
                      onError={handleImageError}
                    />
                    <BookmarkIcon />
                  </div>
                  <div className="project-card-info">
                    <h3 className="project-card-title">{project.title}</h3>
                    <p className="project-card-description">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Fourth Grid Row */}
            <div className="projects-grid-row">
              {secondGridProjects.slice(3, 6).map((project) => (
                <div key={project.id} className="project-card" onClick={() => handleProjectClick(project.id)} style={{ cursor: 'pointer' }}>
                  <div className="project-card-image-wrapper">
                    <img 
                      src={getProjectImage(project)} 
                      alt={project.title}
                      className="project-card-image"
                      onError={handleImageError}
                    />
                    <BookmarkIcon />
                  </div>
                  <div className="project-card-info">
                    <h3 className="project-card-title">{project.title}</h3>
                    <p className="project-card-description">{project.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <Pagination currentPage={currentPage} totalPages={Math.ceil(projects.length / PROJECTS_PER_PAGE)} onPageChange={setCurrentPage} />
        </div>
      </main>

      <Footer />

      {/* Category Modal Overlay */}
      {showCategoryModal && (
        <>
          <div className="category-modal-overlay" onClick={() => setShowCategoryModal(false)}></div>
          <div className="category-modal">
            <div className="category-columns">
              <div className="category-column">
                {leftCategories.map((category, index) => (
                  <div 
                    key={index} 
                    className={`category-item ${category.active ? 'active' : ''}`}
                  >
                    <span className="category-name">{category.name}</span>
                  </div>
                ))}
              </div>
              <div className="category-column">
                {rightCategories.map((category, index) => (
                  <div 
                    key={index} 
                    className={`category-item ${category.active ? 'active' : ''}`}
                  >
                    <span className="category-name">{category.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectsPage;
